// src/components/TextFieldGuides.js
import React, { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Paper,
  Typography,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

/**
 * TextFieldGuides
 *
 * Responsibility:
 *  - Show one or more collapsible "guide" cards below a text field.
 *  - Each guide has its own toggle button (e.g. "Show example").
 *  - Optionally show a right-aligned action (e.g. a Save button).
 *
 * Props:
 *  - guides: Array<{
 *      id: string;
 *      title: string;
 *      content: string;
 *      buttonLabelCollapsed: string;
 *      buttonLabelExpanded: string;
 *      icon?: ReactNode;
 *      defaultOpen?: boolean;
 *    }>
 *  - rightAction?: ReactNode
 */
export default function TextFieldGuides({ guides = [], rightAction }) {
  const [openMap, setOpenMap] = useState(() =>
    guides.reduce((acc, g) => {
      acc[g.id] = !!g.defaultOpen;
      return acc;
    }, {})
  );

  const toggle = (id) => {
    setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (!guides.length && !rightAction) {
    return null;
  }

  return (
    <Box mt={1}>
      {/* Row of toggle buttons (left) + optional rightAction */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={1.5}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {guides.map((guide) => {
            const isOpen = !!openMap[guide.id];
            const label = isOpen
              ? guide.buttonLabelExpanded
              : guide.buttonLabelCollapsed;

            return (
              <Button
                key={guide.id}
                size="small"
                variant="outlined"
                startIcon={guide.icon || null}
                endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => toggle(guide.id)}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 1.5,
                  py: 0.25,
                  fontSize: "0.75rem",
                }}
              >
                {label}
              </Button>
            );
          })}
        </Box>

        {/* Optional right-aligned action, e.g. Save button */}
        {rightAction && <Box>{rightAction}</Box>}
      </Stack>

      {/* Collapsible guide cards */}
      {guides.map((guide) => {
        const isOpen = !!openMap[guide.id];
        const isExample = guide.id === "example";
        const isGuiding = guide.id === "guiding";

        // Base card styles
        let paperSx = {
          mt: 1,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: "grey.50",
          borderColor: "grey.300",
          borderWidth: 1,
          borderStyle: "solid",
        };

        if (isExample) {
          // Example: looks like a sample answer / snippet
          paperSx = {
            ...paperSx,
            backgroundColor: "grey.100",
          };
        } else if (isGuiding) {
          // Guiding questions: more like an instruction panel (no bullets)
          paperSx = {
            ...paperSx,
            backgroundColor: "grey.50",
            borderStyle: "dashed",
          };
        }

        return (
          <Collapse key={guide.id} in={isOpen} unmountOnExit>
            <Paper variant="outlined" sx={paperSx}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                {guide.title}
              </Typography>

              {/* Content: always simple text (no bullet list), preserve line breaks */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.75,
                  whiteSpace: "pre-line",
                  fontStyle: isExample ? "italic" : "normal",
                }}
              >
                {guide.content}
              </Typography>
            </Paper>
          </Collapse>
        );
      })}
    </Box>
  );
}
